/**
 * Admin API — all calls go through /api/v1/
 */

const BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api/v1';
const ADMIN_KEY: string = import.meta.env.VITE_ADMIN_API_KEY || '';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TodayStatus {
  date: {
    day: number;
    month: { ar: string; en: string; index: number };
    year: number;
  };
  method: 'MOON_SIGHTING' | 'CALCULATION';
  confidence: 'CONFIRMED' | 'PENDING' | 'LOW';
  status?: string;
  message?: string;
  source?: string;
  gregorianDate: string;
  moonPhase?: string;
  sightingResult?: 'SEEN' | 'NOT_SEEN';
}

export interface AdminMonth {
  year: number;
  monthIndex: number;
  monthName: { ar: string; en: string };
  status: 'CONFIRMED' | 'PENDING';
  startDate?: string;
  confirmedAt?: string;
  source?: string;
}

export interface EvidenceSource {
  url: string;
  language: string;
  comment?: string;
}

export interface ConfirmMonthRequest {
  locationCode: string;
  year: number;
  month: number;
  startDate: string;
  source: string;
  proofUrls?: string[];
  sources?: EvidenceSource[];
  detectionId?: string;
}

export interface AdjustDateRequest {
  locationCode: string;
  year: number;
  month: number;
  newStartDate: string;
  reason: string;
  note?: string;
  sources?: EvidenceSource[];
}

export interface AuditEntry {
  id: string;
  timestamp: string;
  locationCode: string;
  actionType: 'INITIALIZATION' | 'CONFIRMATION' | 'ADJUSTMENT' | 'CONFIG_CHANGE' | 'ENABLE' | 'DISABLE' | 'RESET';
  previousValue?: unknown;
  newValue?: unknown;
  note?: string;
  user?: string;
  sources?: EvidenceSource[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(ADMIN_KEY && { 'x-admin-key': ADMIN_KEY }),
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─── Public endpoints ─────────────────────────────────────────────────────────

export function getTodayStatus(location: string): Promise<TodayStatus> {
  return request<TodayStatus>(`/today?location=${location}`);
}

// ─── Admin endpoints (/api/v1/admin/*) ───────────────────────────────────────

export function getAdminMonths(location: string): Promise<AdminMonth[]> {
  return request<AdminMonth[]>(`/admin/months?location=${location}`);
}

export async function confirmMonth(data: ConfirmMonthRequest): Promise<void> {
  await request('/admin/confirm-month', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface ConfirmMonthPreview {
  previousConfirmed: { year: number; month: number; monthName: { ar: string; en: string }; startDate: string } | null;
  missingMonths: Array<{ year: number; month: number; monthName: { ar: string; en: string } }>;
  canConfirmDirectly: boolean;
}

export function getConfirmMonthPreview(locationCode: string, year: number, month: number): Promise<ConfirmMonthPreview> {
  return request<ConfirmMonthPreview>(`/admin/confirm-month/preview?location=${locationCode}&year=${year}&month=${month}`);
}

export async function resetLocationHistory(locationCode: string): Promise<{ success: boolean }> {
  return request(`/admin/location/${locationCode}/reset`, {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
}

// ─── Location management ──────────────────────────────────────────────────────

export interface AdditionalAuthority {
  name: string;
  description?: string;
  notes?: string;
}

export type ScrapeType = 'html' | 'rss' | 'google_news';

export interface LocationResource {
  url: string;
  language: string;
  comment?: string;
  active: boolean;
  scrapeType?: ScrapeType;
}

export type CalendarMethod = 'MOON_SIGHTING' | 'CALCULATION' | 'BOTH';

export interface AdminLocationInfo {
  code: string;
  name: string;
  method: CalendarMethod;
  authority?: string;
  timezone: string;
  enabled: boolean;
  // Rich fields
  region?: string;
  authorityEN?: string;
  authorityFR?: string;
  authorityAR?: string;
  additionalAuthorities?: AdditionalAuthority[];
  resources?: LocationResource[];
  notes?: string;
}

export interface CatalogEntry {
  code: string;
  name: string;
  region: string;
  timezone: string;
  defaultMethod: CalendarMethod;
  authorityEN: string;
  authorityFR: string;
  authorityAR: string;
  additionalAuthorities: AdditionalAuthority[];
  defaultSources: Array<{ url: string; language: string; label: string; scrapeType?: ScrapeType }>;
  notes: string;
}

export function getAdminLocations(): Promise<AdminLocationInfo[]> {
  return request<AdminLocationInfo[]>('/admin/locations');
}

export function getAdminLocation(code: string): Promise<AdminLocationInfo> {
  return request<AdminLocationInfo>(`/admin/location/${code}`);
}

export function getCatalog(): Promise<CatalogEntry[]> {
  return request<CatalogEntry[]>('/admin/catalog');
}

export function addCountryFromCatalog(code: string): Promise<{ success: boolean; location: AdminLocationInfo }> {
  return request('/admin/location', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export function updateLocation(
  code: string,
  updates: Partial<AdminLocationInfo & { method?: string }>
): Promise<{ success: boolean; location: AdminLocationInfo }> {
  return request(`/admin/location/${code}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

// ─── Detections ───────────────────────────────────────────────────────────────

export type DetectionStatus = 'PENDING' | 'CONFIRMED' | 'DISMISSED';
export type DetectionConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export interface Detection {
  id: string;
  detectedAt: string;
  locationCode: string;
  source: { type: string; name: string; url: string };
  rawContent: string;
  extracted: {
    hijriYear?: number;
    hijriMonth?: number;
    startDate?: string;
    confidence: DetectionConfidence;
    reasoning: string;
  };
  status: DetectionStatus;
  pipelineRunId?: string;
}

export function getDetections(status?: DetectionStatus): Promise<Detection[]> {
  const qs = status ? `?status=${status}` : '';
  return request<Detection[]>(`/admin/detections${qs}`);
}

export function updateDetectionStatus(id: string, status: 'CONFIRMED' | 'DISMISSED'): Promise<Detection> {
  return request<Detection>(`/admin/detections/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function triggerScraper(force = false): Promise<{ message: string; force: boolean }> {
  return request<{ message: string; force: boolean }>(`/admin/detections/run?force=${force}`, { method: 'POST' });
}

export interface ScraperRunResult {
  detected: number;
  locationsChecked: string[];
  locationsScraped: string[];
  skippedReasons: Record<string, string>;
}

export interface ScraperStatus {
  isRunning: boolean;
  lastRunAt: string | null;
  lastRunResult: ScraperRunResult | null;
  lastRunError: string | null;
  nextRunAt: string | null;
  runCount: number;
}

export function getScraperStatus(): Promise<ScraperStatus> {
  return request<ScraperStatus>('/admin/detections/status');
}

export async function adjustDate(data: AdjustDateRequest): Promise<void> {
  await request('/admin/adjust-date', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getHistory(location: string): Promise<AuditEntry[]> {
  return request<AuditEntry[]>(`/admin/history?location=${location}`);
}

// ─── Pipeline debug ───────────────────────────────────────────────────────────

export type ExcerptOutcome = 'detection' | 'not_announcement' | 'no_api_key' | 'error' | 'duplicate' | 'stale';

export interface ExcerptTrace {
  text: string;
  sourceUrl: string;
  analyzeMs: number;
  outcome: ExcerptOutcome;
  error?: string;
  extracted?: { hijriYear?: number; hijriMonth?: number; startDate?: string; confidence: string; reasoning: string };
  detectionId?: string;
}

export interface SourceTrace {
  name: string;
  type: string;
  url: string;
  fetchMs: number;
  status: 'ok' | 'error';
  error?: string;
  excerptCount: number;
  excerpts: ExcerptTrace[];
}

export interface LocationTrace {
  locationCode: string;
  dayCheckMs: number;
  hijriDay: number | null;
  decision: 'scrape' | 'skip' | 'error';
  decisionReason: string;
  sources: SourceTrace[];
}

export interface PipelineRunSummary {
  id: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  forced: boolean;
  totalExcerpts: number;
  totalAnalyzed: number;
  totalDetections: number;
  totalErrors: number;
  locationsScraped: number;
  locationsSkipped: number;
}

export interface PipelineRun extends PipelineRunSummary {
  locations: LocationTrace[];
}

export function getDebugRuns(): Promise<PipelineRunSummary[]> {
  return request<PipelineRunSummary[]>('/admin/detections/runs');
}

export function getDebugRun(id: string): Promise<PipelineRun> {
  return request<PipelineRun>(`/admin/detections/runs/${id}`);
}

// ─── Waitlist subscriptions ───────────────────────────────────────────────────

export interface Subscription {
  email: string;
  countryCode: string;
  subscribedAt: string;
}

export function getSubscriptions(): Promise<Subscription[]> {
  return request<Subscription[]>('/admin/subscriptions');
}

// ─── Morocco-specific constants ───────────────────────────────────────────────

export const MOROCCO_SOURCE = 'Ministry of Awqaf and Islamic Affairs - Morocco';
export const MOROCCO_CODE = 'MA';
